/**
 * Malay AI Lesson Tutor activity registry (Phase 1C).
 * Reading Comprehension Tutor lives on /student/reading-comprehension only.
 */

export type MalayAiTutorActivityKey = 'malay_unit_5';

/** @deprecated Legacy key — redirect to dedicated reading page; not in activity picker. */
export type MalayLegacyReadingActivityKey = 'malay_jendela_seni_reading';

export type MalayAiTutorActivityType = 'lesson_teaching' | 'reading_comprehension';

export interface MalayAiTutorActivity {
  key: MalayAiTutorActivityKey;
  activityType: MalayAiTutorActivityType;
  runtime: 'ai_lesson_tutor';
  title: string;
  categoryLabel: string;
  description: string;
  topics: string[];
  ctaLabel: string;
}

type ActivityDef = {
  key: MalayAiTutorActivityKey;
  activityType: MalayAiTutorActivityType;
  runtime: 'ai_lesson_tutor';
  title: string;
  topicCount: number;
};

const ACTIVITY_DEFS: readonly ActivityDef[] = [
  {
    key: 'malay_unit_5',
    activityType: 'lesson_teaching',
    runtime: 'ai_lesson_tutor',
    title: 'Lain Dulang, Lain Kaki',
    topicCount: 6,
  },
] as const;

export const MALAY_LEGACY_READING_ACTIVITY_KEY = 'malay_jendela_seni_reading' as const;

/** English defaults used when `t` is not provided (tests / non-i18n callers). */
const ENGLISH_UI: Record<
  MalayAiTutorActivityKey,
  Pick<MalayAiTutorActivity, 'categoryLabel' | 'description' | 'topics' | 'ctaLabel'>
> = {
  malay_unit_5: {
    categoryLabel: 'Unit 5 Teaching',
    description: 'Learn about food, culture, language, vocabulary and expository writing.',
    topics: [
      'Food and cultural identity',
      'Traditional and fusion food',
      'Vocabulary',
      'ialah and adalah',
      'Proverbs and pantun',
      'Expository-writing introductions',
    ],
    ctaLabel: 'Start Lesson',
  },
};

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

function localizeActivity(def: ActivityDef, t?: TranslateFn): MalayAiTutorActivity {
  const fallback = ENGLISH_UI[def.key];
  if (!t) {
    return { ...def, ...fallback };
  }
  const base = `tutor.activities.${def.key}`;
  const topics = Array.from({ length: def.topicCount }, (_, i) =>
    t(`${base}.topics.${i}`, { defaultValue: fallback.topics[i] })
  );
  return {
    key: def.key,
    activityType: def.activityType,
    runtime: def.runtime,
    title: def.title,
    categoryLabel: t(`${base}.categoryLabel`, { defaultValue: fallback.categoryLabel }),
    description: t(`${base}.description`, { defaultValue: fallback.description }),
    topics,
    ctaLabel: t(`${base}.ctaLabel`, { defaultValue: fallback.ctaLabel }),
  };
}

const MALAY_ACTIVITY_BY_KEY: Record<MalayAiTutorActivityKey, ActivityDef> =
  ACTIVITY_DEFS.reduce(
    (acc, activity) => {
      acc[activity.key] = activity;
      return acc;
    },
    {} as Record<MalayAiTutorActivityKey, ActivityDef>
  );

export const MALAY_AI_TUTOR_ACTIVITIES: readonly MalayAiTutorActivity[] = ACTIVITY_DEFS.map((d) =>
  localizeActivity(d)
);

export function isMalayAiTutorActivityKey(value: string | null | undefined): value is MalayAiTutorActivityKey {
  if (!value) return false;
  return Object.prototype.hasOwnProperty.call(MALAY_ACTIVITY_BY_KEY, value);
}

export function isMalayLegacyReadingActivityKey(value: string | null | undefined): boolean {
  return (value?.trim() ?? '') === MALAY_LEGACY_READING_ACTIVITY_KEY;
}

export function getMalayAiTutorActivity(
  key: string | null | undefined,
  t?: TranslateFn
): MalayAiTutorActivity | null {
  if (!isMalayAiTutorActivityKey(key)) return null;
  return localizeActivity(MALAY_ACTIVITY_BY_KEY[key], t);
}

export function listMalayAiTutorActivities(t?: TranslateFn): readonly MalayAiTutorActivity[] {
  return ACTIVITY_DEFS.map((d) => localizeActivity(d, t));
}

export function listLocalizedMalayAiTutorActivities(t: TranslateFn): readonly MalayAiTutorActivity[] {
  return listMalayAiTutorActivities(t);
}
